import React, { useState, useEffect, useRef } from 'react';
import {
  Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin, Heart,
  MessageCircle, X, Send, Share2, Copy, Check
} from 'lucide-react';
import { ChevronsUp } from 'lucide-react';
import { usePublic } from '../../../../context/PublicContext';
import { env } from '../../../../config/env';

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const Footer: React.FC = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [copied, setCopied] = useState(false);
  const [whatsAppMessage, setWhatsAppMessage] = useState('');
  const shareMenuRef = useRef<HTMLDivElement>(null);

  const { settings, isLoadingSettings } = usePublic();

  useEffect(() => {
    const handleScroll = () => setShowScrollButton(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    if (showShareMenu) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  const handleSendWhatsApp = () => {
    if (!settings?.whatsapp_number) {
      alert('Numéro WhatsApp non configuré');
      return;
    }
    const phone = settings.whatsapp_number.replace(/\D/g, '');
    const text = encodeURIComponent(whatsAppMessage || 'Bonjour, je viens de votre site web.');
    window.open(`https://wa.me/${phone}?text=${text}`, '_blank');
    setShowWhatsAppModal(false);
    setWhatsAppMessage('');
  };

  // ---------- Partage ----------
  const siteUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = settings?.company_name || "L'EFFICACITE DES PSAUMES ET PLANTES";
  const shareText = settings?.slogan || "Découvrez l'alliance sacrée des Psaumes et des plantes.";

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: siteUrl });
        return;
      } catch { /* annulé */ }
    }
    setShowShareMenu((v) => !v);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(siteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Impossible de copier le lien');
    }
  };

  const openShare = (network: 'facebook' | 'twitter' | 'whatsapp') => {
    const url = encodeURIComponent(siteUrl);
    const text = encodeURIComponent(shareText);
    const links = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      twitter: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
    };
    window.open(links[network], '_blank', 'noopener,noreferrer,width=600,height=500');
    setShowShareMenu(false);
  };

  if (isLoadingSettings) {
    return (
      <footer className="bg-accent/70 text-primary py-6 p-4 mt-5">
        <div className="max-w-7xl mx-auto text-center">
          <p>Chargement des informations...</p>
        </div>
      </footer>
    );
  }

  // ---------- Données depuis settings ----------
  const companyName = settings?.company_name || "L'EFFICACITE DES PSAUMES ET PLANTES";
  const slogan = settings?.slogan || "L'alliance sacrée des Psaumes et des plantes médicinales pour votre bien-être quotidien.";
  const logoUrl = settings?.logo
    ? `${env.IMAGE_BASE_URL_SANS_STORAGE}${settings.logo}`
    : "/assets/logo/logoPsaumesetplantes-removebg-preview.png";

  const email = settings?.email || "judesessou@gmail.com";
  const secondaryEmail = settings?.secondary_email;
  const phone = settings?.phone;
  const secondaryPhone = settings?.secondary_phone;
  const whatsappNumber = settings?.whatsapp_number || "2290197737703";
  const businessHours = settings?.business_hours;

  // Adresse multi-lignes
  const addressLines: string[] = [];
  if (settings?.address) addressLines.push(settings.address);
  if (settings?.address_line_2) addressLines.push(settings.address_line_2);
  const cityLine = [
    settings?.postal_code,
    settings?.city,
    settings?.state
  ].filter(Boolean).join(' ');
  if (cityLine) addressLines.push(cityLine);
  if (settings?.country) addressLines.push(settings.country);

  const finalAddressLines = addressLines.length > 0 ? addressLines : ["123 Rue Tori, Bénin"];

  const socialLinks = settings?.social_links || {
    facebook: "https://www.facebook.com/j2d2s/",
    instagram: "https://www.instagram.com/j2d2s/",
    linkedin: "",
    youtube: "",
    tiktok: ""
  };

  return (
    <footer className="bg-accent/70 text-primary py-6 p-4 mt-5">
      <div className="max-w-7xl mx-auto">
        {/* Contenu principal */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Colonne 1 : Logo + slogan */}
          <div className='space-y-3'>
            <div
              className="flex items-center justify-center cursor-pointer"
              onClick={() => (window.location.href = '/')}
            >
              <img src={logoUrl} alt={companyName} className="h-24 w-auto object-contain" />
              <span className="text-2xl font-bold tracking-tight">{companyName}</span>
            </div>
            <p className="text-primary/70 text-sm leading-relaxed">{slogan}</p>
          </div>

          {/* Colonne 2 : Liens utiles (desktop) */}
          <div className='md:ml-12 hidden md:block'>
            <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
              Liens utiles
            </h3>
            <ul className="space-y-2 text-sm">
              <li><a href="/" className="text-primary/70 hover:text-secondary transition">Accueil</a></li>
              <li><a href="/about" className="text-primary/70 hover:text-secondary transition">À propos</a></li>
              <li><a href="/products" className="text-primary/70 hover:text-secondary transition">Nos produits</a></li>
              <li><a href="/contact" className="text-primary/70 hover:text-secondary transition">Contact</a></li>
            </ul>
          </div>

          {/* Colonne 3 : Contact */}
          <div className='flex flex-col gap-4'>
            <div>
              <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
                Contact
              </h3>
              <ul className="space-y-2 text-sm">
                {phone && (
                  <li className="flex items-center gap-2 text-primary/70">
                    <Phone size={16} className="text-primary shrink-0" />
                    <a href={`tel:${phone.replace(/\s/g, '')}`} className="hover:text-secondary transition break-words">
                      {phone}
                    </a>
                  </li>
                )}
                {secondaryPhone && (
                  <li className="flex items-center gap-2 text-primary/70">
                    <Phone size={16} className="text-primary shrink-0" />
                    <a href={`tel:${secondaryPhone.replace(/\s/g, '')}`} className="hover:text-secondary transition break-words">
                      {secondaryPhone}
                    </a>
                  </li>
                )}

                <li className="flex items-center gap-2 text-primary/70">
                  <Mail size={16} className="text-primary shrink-0" />
                  <a href={`mailto:${email}`} className="hover:text-secondary transition break-all">
                    {email}
                  </a>
                </li>
                {secondaryEmail && secondaryEmail !== email && (
                  <li className="flex items-center gap-2 text-primary/70">
                    <Mail size={16} className="text-primary shrink-0" />
                    <a href={`mailto:${secondaryEmail}`} className="hover:text-secondary transition break-all">
                      {secondaryEmail}
                    </a>
                  </li>
                )}

                <li className="flex items-start gap-2 text-primary/70">
                  <MapPin size={16} className="text-primary shrink-0 mt-0.5" />
                  <address className="not-italic break-words leading-relaxed">
                    {finalAddressLines.map((line, i) => (
                      <span key={i} className="block">{line}</span>
                    ))}
                  </address>
                </li>
              </ul>
            </div>

            {/* Réseaux sociaux */}
            <div>
              <h3 className="text-md font-semibold mb-2">Suivez-nous</h3>
              <div className="flex gap-3 flex-wrap">
                {socialLinks.facebook && (
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <Facebook size={16} className="group-hover:scale-110 transition" />
                  </a>
                )}
                {socialLinks.instagram && (
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <Instagram size={16} className="group-hover:scale-110 transition" />
                  </a>
                )}
                {socialLinks.youtube && (
                  <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <Youtube size={16} className="group-hover:scale-110 transition" />
                  </a>
                )}
                {whatsappNumber && (
                  <a href={`https://wa.me/${whatsappNumber.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-primary hover:text-white transition-all duration-300 group">
                    <MessageCircle size={16} className="group-hover:scale-110 transition" />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Colonne 4 : Horaires (desktop) */}
          {businessHours && (
            <div className="hidden md:block">
              <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
                Horaires
              </h3>
              <ul className="space-y-1 text-sm">
                <li className="flex justify-between"><span>Lundi :</span> <span>{businessHours.monday}</span></li>
                <li className="flex justify-between"><span>Mardi :</span> <span>{businessHours.tuesday}</span></li>
                <li className="flex justify-between"><span>Mercredi :</span> <span>{businessHours.wednesday}</span></li>
                <li className="flex justify-between"><span>Jeudi :</span> <span>{businessHours.thursday}</span></li>
                <li className="flex justify-between"><span>Vendredi :</span> <span>{businessHours.friday}</span></li>
                <li className="flex justify-between"><span>Samedi :</span> <span>{businessHours.saturday}</span></li>
                <li className="flex justify-between"><span>Dimanche :</span> <span>{businessHours.sunday}</span></li>
              </ul>
            </div>
          )}
        </div>

        {/* Mobile : Liens + Horaires */}
        <div className="grid grid-cols-2 gap-8 mt-8 md:hidden">
          <div>
            <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
              Liens utiles
            </h3>
            <ul className="space-y-2 text-sm">
              <li><a href="/" className="text-primary/70 hover:text-secondary transition">Accueil</a></li>
              <li><a href="/about" className="text-primary/70 hover:text-secondary transition">À propos</a></li>
              <li><a href="/products" className="text-primary/70 hover:text-secondary transition">Nos produits</a></li>
              <li><a href="/contact" className="text-primary/70 hover:text-secondary transition">Contact</a></li>
            </ul>
          </div>
          {businessHours && (
            <div className="relative pl-4 sm:pl-6">
              <div className="absolute left-0 top-0 h-full w-px bg-primary/70"></div>
              <h3 className="text-lg font-semibold mb-3 relative inline-block after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-6 after:h-0.5 after:bg-accent">
                Horaires
              </h3>
              <ul className="space-y-1 text-xs">
                <li className="flex justify-between"><span>Lun :</span> <span>{businessHours.monday}</span></li>
                <li className="flex justify-between"><span>Mar :</span> <span>{businessHours.tuesday}</span></li>
                <li className="flex justify-between"><span>Mer :</span> <span>{businessHours.wednesday}</span></li>
                <li className="flex justify-between"><span>Jeu :</span> <span>{businessHours.thursday}</span></li>
                <li className="flex justify-between"><span>Ven :</span> <span>{businessHours.friday}</span></li>
                <li className="flex justify-between"><span>Sam :</span> <span>{businessHours.saturday}</span></li>
                <li className="flex justify-between"><span>Dim :</span> <span>{businessHours.sunday}</span></li>
              </ul>
            </div>
          )}
        </div>

        {/* Copyright */}
        <div className="border-t border-white/10 mt-8 pt-6 text-center text-primary text-sm">
          <p>
            &copy; {new Date().getFullYear()} {companyName}. Conçu avec{' '}
            <Heart size={12} className="inline text-primary" /> pour votre bien-être.
          </p>
        </div>
      </div>

      {/* ================= BOUTONS FLOTTANTS ================= */}
      {/* Mobile : bottom-20 pour passer au-dessus de la BottomBar (~52px) */}
      {/* Desktop : md:bottom-6 (pas de BottomBar) */}
      <div className="fixed bottom-20 right-4 z-40 flex flex-col items-end gap-2 md:bottom-6 md:right-6">
        {/* Bouton PARTAGE */}
        <div className="relative" ref={shareMenuRef}>
          <button
            onClick={handleShare}
            className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full shadow-md hover:scale-110 transition-all duration-300"
            aria-label="Partager le site"
            title="Partager le site"
          >
            <Share2 size={18} />
          </button>

          {showShareMenu && (
            <div className="absolute bottom-full right-0 mb-2 w-52 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100 animate-in slide-in-from-bottom-2 fade-in duration-200">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-gray-700 text-[11px] font-semibold uppercase tracking-wide">Partager</p>
              </div>
              <button onClick={() => openShare('facebook')} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition">
                <Facebook size={16} className="text-blue-600" /> Facebook
              </button>
              <button onClick={() => openShare('twitter')} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-sky-50 hover:text-sky-700 transition">
                <Twitter size={16} className="text-sky-500" /> Twitter / X
              </button>
              <button onClick={() => openShare('whatsapp')} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-green-50 hover:text-green-700 transition">
                <MessageCircle size={16} className="text-green-600" /> WhatsApp
              </button>
              <button onClick={handleCopyLink} className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition border-t border-gray-100">
                {copied ? (
                  <><Check size={16} className="text-green-600" /> <span className="text-green-600 font-medium">Lien copié !</span></>
                ) : (
                  <><Copy size={16} className="text-gray-500" /> Copier le lien</>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Bouton WHATSAPP */}
        {whatsappNumber && (
          <button
            onClick={() => setShowWhatsAppModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white p-2 rounded-full shadow-md hover:scale-110 transition-all duration-300"
            aria-label="Contact WhatsApp"
            title="Contacter sur WhatsApp"
          >
            <MessageCircle size={18} />
          </button>
        )}

        {/* Bouton SCROLL TO TOP */}
        {showScrollButton && (
          <button
            onClick={scrollToTop}
            className="bg-secondary text-white p-2 rounded-full shadow-md hover:scale-110 transition-all duration-300"
            aria-label="Remonter en haut"
            title="Remonter en haut"
          >
            <ChevronsUp size={18} />
          </button>
        )}
      </div>

      {/* ================= MODAL WHATSAPP ================= */}
      {showWhatsAppModal && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowWhatsAppModal(false)} />
          <div className="fixed bottom-36 right-4 md:bottom-24 md:right-6 z-50 w-72 bg-white rounded-xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-2 fade-in duration-200">
            <div className="bg-green-800 text-white px-4 py-2.5 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <MessageCircle size={18} />
                <span className="font-semibold text-sm">WhatsApp</span>
              </div>
              <button onClick={() => setShowWhatsAppModal(false)} className="text-white/80 hover:text-white transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 text-xs mb-3">Envoyez-nous un message</p>
              <textarea
                value={whatsAppMessage}
                onChange={(e) => setWhatsAppMessage(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
                placeholder="Bonjour, je souhaite en savoir plus sur vos produits..."
              />
              <button
                onClick={handleSendWhatsApp}
                className="w-full mt-3 bg-green-600 hover:bg-green-700 text-white font-semibold py-2 rounded-lg transition flex items-center justify-center gap-2 text-sm"
              >
                <Send size={14} /> Envoyer
              </button>
            </div>
          </div>
        </>
      )}
    </footer>
  );
};

export default Footer;